// O modo "escolher as fotos": o editor marca, entre as imagens de dentro da
// página, as que entram na galeria e escreve uma legenda para cada uma. O bloco
// guarda `modo: 'escolhidas'` e `escolhidas: [{ uid, id, legenda }]`.
//
// Só filtra o resultado da busca da galeria (as imagens filhas da página): um
// UID gravado no bloco nunca traz foto de fora, nem foto que o leitor não pode
// ver.
export const MODO_TODAS = 'todas';
export const MODO_ESCOLHA = 'escolhidas';

// Só o valor exato filtra. Bloco sem modo (galeria antiga, leitura no
// servidor), null ou outro valor qualquer mostram todas as imagens.
export const emEscolha = (data) => data?.modo === MODO_ESCOLHA;

// Nome curto da imagem dentro da página: o último pedaço do endereço.
export const idDaImagem = (imagem) =>
  (imagem?.['@id'] || '').replace(/\/+$/, '').split('/').pop();

// A imagem na escolha: o UID; sem ele, o endereço.
export const chaveDaImagem = (imagem) => imagem.UID || imagem['@id'];

// A legenda gravada, só se for texto: um bloco gravado por API, migração ou
// JSON colado pode trazer outra coisa, e a página não pode cair por isso.
export const legendaDe = (entrada) =>
  typeof entrada?.legenda === 'string' ? entrada.legenda : '';

// Para cada imagem da página, a entrada da escolha que é dela, pelo UID. Pelo
// nome curto só quando nenhum UID gravado está entre as imagens: é a página
// copiada, em que o Plone dá UID novo a todas as imagens da cópia. Na página
// de origem, foto apagada e outra enviada com o mesmo nome não herdam a
// escolha nem a legenda. `sumidas` conta as entradas que não acham imagem
// nenhuma (fotos que saíram da página); a mesma foto repetida não conta.
export function casar(imagens, escolhidas) {
  const entradas = (Array.isArray(escolhidas) ? escolhidas : []).filter(
    (e) => e && typeof e === 'object' && (e.uid || e.id),
  );
  const uids = new Set(imagens.map((img) => img.UID).filter(Boolean));
  const ids = new Set(imagens.map(idDaImagem));
  const peloNome = !entradas.some((e) => e.uid && uids.has(e.uid));
  const porUid = new Map();
  const porId = new Map();
  entradas.forEach((e) => {
    if (e.uid && uids.has(e.uid)) {
      if (!porUid.has(e.uid)) porUid.set(e.uid, e);
    } else if (peloNome && e.id && !porId.has(e.id)) porId.set(e.id, e);
  });
  const casadas = new Map();
  imagens.forEach((img) => {
    const entrada =
      (img.UID && porUid.get(img.UID)) || porId.get(idDaImagem(img));
    if (entrada) casadas.set(chaveDaImagem(img), entrada);
  });
  const naPagina = (e) =>
    (e.uid && uids.has(e.uid)) || (peloNome && e.id && ids.has(e.id));
  const sumidas = entradas.filter((e) => !naPagina(e)).length;
  return { casadas, sumidas };
}

// As fotos que a galeria mostra, na ordem da busca (a da tela Conteúdo). No
// modo de escolha, só as escolhidas, com a legenda do bloco no lugar da
// descrição da imagem; legenda em branco deixa a descrição.
export function fotosDaGaleria(imagens, data) {
  if (!emEscolha(data)) return { fotos: imagens, sumidas: 0 };
  const { casadas, sumidas } = casar(imagens, data.escolhidas);
  const fotos = imagens
    .filter((img) => casadas.has(chaveDaImagem(img)))
    .map((img) => {
      const legenda = legendaDe(casadas.get(chaveDaImagem(img))).trim();
      return legenda ? { ...img, description: legenda } : img;
    });
  return { fotos, sumidas };
}

// O que a barra lateral grava. Cada mudança regrava a lista inteira: na ordem
// da tela Conteúdo, com o UID atual (a cópia da página se acerta aqui) e sem
// as fotos que saíram da página.
const legendasAtuais = (imagens, escolhidas) =>
  new Map(
    [...casar(imagens, escolhidas).casadas].map(([chave, entrada]) => [
      chave,
      legendaDe(entrada),
    ]),
  );

const textoOuVazio = (texto) => (typeof texto === 'string' ? texto : '');

const lista = (imagens, legendas) =>
  imagens
    .filter((img) => legendas.has(chaveDaImagem(img)))
    .map((img) => ({
      uid: img.UID,
      id: idDaImagem(img),
      legenda: legendas.get(chaveDaImagem(img)),
    }));

// Ao marcar, a foto entra com `legendaAoMarcar`: a barra lateral devolve a
// legenda de quem foi desmarcado sem querer na mesma edição.
export function alternar(imagens, escolhidas, imagem, legendaAoMarcar = '') {
  const legendas = legendasAtuais(imagens, escolhidas);
  const chave = chaveDaImagem(imagem);
  if (legendas.has(chave)) legendas.delete(chave);
  else legendas.set(chave, textoOuVazio(legendaAoMarcar));
  return lista(imagens, legendas);
}

// A legenda vai como foi digitada: aparar a cada tecla comeria o espaço entre
// as palavras. A galeria apara ao mostrar.
export function legendar(imagens, escolhidas, imagem, texto) {
  const legendas = legendasAtuais(imagens, escolhidas);
  legendas.set(chaveDaImagem(imagem), texto);
  return lista(imagens, legendas);
}

// Acrescenta as que faltam, com a legenda guardada se houver, e mantém as
// legendas já escritas.
export function marcarTodas(imagens, escolhidas, guardadas = new Map()) {
  const legendas = legendasAtuais(imagens, escolhidas);
  imagens.forEach((img) => {
    const chave = chaveDaImagem(img);
    if (!legendas.has(chave))
      legendas.set(chave, textoOuVazio(guardadas.get(chave)));
  });
  return lista(imagens, legendas);
}

// "Tirar da lista": regrava sem as fotos que saíram da página. Sem isso, a
// legenda de uma foto apagada continua no bloco, que vai inteiro para quem lê.
export const tirarSumidas = (imagens, escolhidas) =>
  lista(imagens, legendasAtuais(imagens, escolhidas));
